import { useRecordContext } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { differenceInDays } from "date-fns";
import { FileText, Target, Sparkles } from "lucide-react";
import type { Contact } from "../types";

export const RelationshipBrief = () => {
  const record = useRecordContext<Contact>();

  if (!record) return null;

  const daysSinceInteraction = record.last_meaningful_interaction_at
    ? differenceInDays(
        new Date(),
        new Date(record.last_meaningful_interaction_at),
      )
    : null;

  const getAgingBadge = () => {
    if (daysSinceInteraction === null) return null;
    if (daysSinceInteraction > 60)
      return (
        <Badge variant="destructive" className="ml-2">
          Stale (&gt;60 days)
        </Badge>
      );
    if (daysSinceInteraction > 30)
      return (
        <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800">
          Aging (&gt;30 days)
        </Badge>
      );
    return (
      <Badge
        variant="outline"
        className="ml-2 border-green-200 text-green-700 bg-green-50"
      >
        Recent
      </Badge>
    );
  };

  // Resolve David A. Fields Classifications
  const strengthCode =
    record.status === "A"
      ? "A"
      : record.status === "B"
        ? "B"
        : record.status === "C"
          ? "C"
          : null;
  const roleCode =
    record.influence === "1"
      ? "1"
      : record.influence === "2"
        ? "2"
        : record.influence === "3"
          ? "3"
          : null;
  const classification =
    strengthCode && roleCode ? `${strengthCode}${roleCode}` : null;

  const isNetworkCore =
    classification === "A1" ||
    classification === "A2" ||
    classification === "B1";

  const getStrengthLabel = () => {
    if (record.status === "A") return "A - Strong (Pick up phone)";
    if (record.status === "B") return "B - Medium (Recognize name)";
    if (record.status === "C") return "C - Weak (Peripheral/Unknown)";
    // Outside the Fields A/B/C scale: an explicit "we have never interacted",
    // which is the honest answer for most of the bulk LinkedIn import.
    if (record.status === "None") return "None - no relationship";
    return record.status || "Unknown";
  };

  const getRoleLabel = () => {
    if (record.influence === "1")
      return "1 - Decision Maker (Power of the purse)";
    if (record.influence === "2") return "2 - Influencer (Can refer/connector)";
    if (record.influence === "3") return "3 - Neither";
    return record.influence || "Unspecified";
  };

  return (
    <Card className="mb-4 bg-muted/30 border-primary/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-primary" />
            Strategic Relationship Brief
          </span>
          {isNetworkCore && (
            <Badge className="bg-green-600 hover:bg-green-700 text-white font-bold flex items-center gap-1 shadow-sm">
              💎 Network Core ({classification})
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Relationship Strength
            </span>
            <div className="font-medium mt-1">
              {getStrengthLabel()}
              {getAgingBadge()}
            </div>
          </div>
          <div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Influence (Purchasing Power)
            </span>
            <div className="font-medium mt-1">{getRoleLabel()}</div>
          </div>
          {classification && (
            <div>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Category Classification
              </span>
              <div className="font-bold text-primary mt-1 text-base">
                {classification}
              </div>
            </div>
          )}
        </div>

        <Separator />

        <div>
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center">
            <Target className="w-3 h-3 mr-1" />
            Recommended Next Move
          </span>
          <div className="mt-1 text-sm bg-primary/10 text-primary-foreground p-2 rounded-md font-medium text-black dark:text-white">
            {record.next_relationship_move ||
              "No move specified - Needs Review"}
          </div>
        </div>

        {record.relationship_evidence && (
          <div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center">
              <FileText className="w-3 h-3 mr-1" />
              Evidence & Context
            </span>
            <div className="mt-1 text-sm italic text-muted-foreground">
              "{record.relationship_evidence}"
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
